import type { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';

import type { SymbolDTO, SymbolPackDTO } from '@nora-monorepo/nora-api-client';
import { SymbolUsageEnum } from '@nora-monorepo/nora-api-client';
import {
  isNonEmptyString,
  svgContainsColorPlaceholder,
  tintSvg,
} from '@nora-monorepo/shared';
import {
  appLogger,
  ConnectivityStatus,
  symbolIdbCache,
  symbolPackIdbCache,
  symbolPackListIdbCache,
  useApiStore,
  useGenericAppStore,
} from '@nora-monorepo/ui-shared';

const DEFAULT_TINT_COLOR = '#000000';
const SVG_CONTENT_TYPE = 'image/svg+xml';

const PREVIEW_TINT_COLOR = '#888888';

export function resolvePreviewTintColor(): string {
  return PREVIEW_TINT_COLOR;
}

export const useSymbolStore = defineStore('symbolStore', () => {
  const api = useApiStore();
  const genericAppStore = useGenericAppStore();

  const symbolUrlCache = new Map<string, string>();
  const inflightRequests = new Map<string, Promise<string>>();

  /** Cache key for a symbol's blob in IndexedDB. */
  function getBaseKey(packId: string, symbolId: string): string {
    return `${packId}:${symbolId}`;
  }

  /** In-memory cache key; includes the colour so each tint variant is cached separately. */
  function getMemoryKey(packId: string, symbolId: string, color: string): string {
    return `${packId}:${symbolId}:${color}`;
  }

  /** Content type from the response header, falling back to the blob's type then a generic default. */
  function resolveContentType(response: AxiosResponse<Blob>, blob: Blob): string {
    const header: unknown = response.headers['content-type'];

    if (typeof header === 'string') {
      return header;
    }

    if (Array.isArray(header) && typeof header[0] === 'string') {
      return header[0];
    }

    return blob.type || 'application/octet-stream';
  }

  /** Object URL for a symbol blob, tinting SVGs that contain a colour placeholder. */
  async function buildObjectUrl(record: { blob: Blob; contentType: string }, color: string): Promise<string> {
    if (record.contentType !== SVG_CONTENT_TYPE) {
      return URL.createObjectURL(record.blob);
    }

    const svg = await record.blob.text();

    if (!svgContainsColorPlaceholder(svg)) {
      return URL.createObjectURL(record.blob);
    }

    const tinted = new Blob([tintSvg(svg, color)], { type: SVG_CONTENT_TYPE });

    return URL.createObjectURL(tinted);
  }

  /** Returns the symbol blob from IndexedDB, downloading and caching it on a miss. Throws when offline and uncached. */
  async function loadSymbolRecord(packId: string, symbolId: string): Promise<{ blob: Blob; contentType: string }> {
    const baseKey = getBaseKey(packId, symbolId);
    const cached = await symbolIdbCache.get(baseKey);

    if (cached) {
      return cached;
    }

    if (genericAppStore.connectivityStatus === ConnectivityStatus.OFFLINE) {
      throw new Error(`Symbol not cached and offline: ${baseKey}`);
    }

    const response = await api.symbol.downloadSymbol(
      { packId, symbolId },
      { responseType: 'blob' },
    );

    const blob = response.data;

    if (blob === undefined) {
      throw new Error(`Failed to download symbol: ${baseKey}`);
    }

    const contentType = resolveContentType(response, blob);

    await symbolIdbCache.put({ key: baseKey, packId, symbolId, blob, contentType });

    return { blob, contentType };
  }

  /**
   * Object URL for a symbol, tinted with `color`. Served from the in-memory cache, then
   * IndexedDB, then the network. Concurrent requests for the same key are coalesced.
   */
  async function getSymbolObjectUrl(
    packId: string,
    symbolId: string,
    color?: string,
  ): Promise<string> {
    const resolvedColor = color ?? DEFAULT_TINT_COLOR;
    const memoryKey = getMemoryKey(packId, symbolId, resolvedColor);

    const memoryHit = symbolUrlCache.get(memoryKey);

    if (memoryHit !== undefined) {
      return memoryHit;
    }

    const inflight = inflightRequests.get(memoryKey);

    if (inflight !== undefined) {
      return await inflight;
    }

    const request = loadSymbolRecord(packId, symbolId)
      .then(async record => await buildObjectUrl(record, resolvedColor))
      .then((url) => {
        symbolUrlCache.set(memoryKey, url);
        return url;
      })
      .finally(() => {
        inflightRequests.delete(memoryKey);
      });

    inflightRequests.set(memoryKey, request);

    return await request;
  }

  /** Whether the symbol's object URL is in the in-memory cache. Does not check IndexedDB. */
  function isCached(packId: string, symbolId: string, color = DEFAULT_TINT_COLOR): boolean {
    return symbolUrlCache.has(getMemoryKey(packId, symbolId, color));
  }

  /** The in-memory object URL for a symbol, or undefined if it isn't held in memory. */
  function getCachedUrl(packId: string, symbolId: string, color = DEFAULT_TINT_COLOR): string | undefined {
    return symbolUrlCache.get(getMemoryKey(packId, symbolId, color));
  }

  /** Revokes a symbol's object URL and drops it from memory; the IndexedDB copy is kept. */
  function revokeSymbol(packId: string, symbolId: string, color = DEFAULT_TINT_COLOR): void {
    const memoryKey = getMemoryKey(packId, symbolId, color);
    const url = symbolUrlCache.get(memoryKey);

    if (isNonEmptyString(url)) {
      URL.revokeObjectURL(url);
      symbolUrlCache.delete(memoryKey);
    }
  }

  /** Revokes every in-memory object URL and clears the in-memory and inflight caches. */
  function cleanup(): void {
    for (const url of symbolUrlCache.values()) {
      URL.revokeObjectURL(url);
    }

    symbolUrlCache.clear();
    inflightRequests.clear();
  }

  /** `updatedAt` as a timestamp. It's an ISO string from the API but a Date when restored from IndexedDB. */
  function toUpdatedAtToken(pack: SymbolPackDTO): number {
    return new Date(pack.updatedAt).getTime();
  }

  /**
   * Whether a cached pack differs from the freshly fetched one. `updatedAt` covers all server-side
   * changes (the API bumps it on symbol-level edits like favourite toggles); `symbolCount` is a fallback.
   */
  function hasPackChanged(cachedPack: SymbolPackDTO, freshPack: SymbolPackDTO): boolean {
    return toUpdatedAtToken(cachedPack) !== toUpdatedAtToken(freshPack)
      || cachedPack.symbolCount !== freshPack.symbolCount;
  }

  /**
   * Invalidates the detail cache of any pack that changed or was removed server-side. Unchanged
   * packs keep their cached details, so later getSymbolPack calls stay fast and offline-capable.
   */
  async function reconcileChangedPacks(cachedPacks: SymbolPackDTO[], freshPacks: SymbolPackDTO[]): Promise<void> {
    const freshPacksById = new Map(freshPacks.map(freshPack => [freshPack.id, freshPack]));

    for (const cachedPack of cachedPacks) {
      const freshPack = freshPacksById.get(cachedPack.id);

      // Removed server-side or changed: drop its cached detail.
      if (!freshPack || hasPackChanged(cachedPack, freshPack)) {
        await invalidatePackDetailCache(cachedPack.id);
      }
    }
  }

  /**
   * Symbol packs for a usage type. Online, refreshes from the server and invalidates the detail
   * cache of any changed or removed pack; offline or on error, falls back to the IndexedDB copy.
   */
  const getSymbolPacks = async (usage: SymbolUsageEnum): Promise<SymbolPackDTO[]> => {
    const cached = await symbolPackListIdbCache.getList(usage);

    if (genericAppStore.connectivityStatus === ConnectivityStatus.OFFLINE) {
      return cached ?? [];
    }

    try {
      const response = await api.unwrap(api.symbol.listSymbolPacks({
        usage,
      }));

      const freshPacks = response.data ?? [];

      await reconcileChangedPacks(cached ?? [], freshPacks);
      await symbolPackListIdbCache.setList(usage, freshPacks);

      return freshPacks;
    }
    catch (error) {
      console.warn('Failed to refresh symbol pack list, using cached data', error);

      if (cached) {
        return cached;
      }

      throw error;
    }
  };

  /**
   * A symbol pack by id, cache-first. getSymbolPacks keeps the detail cache fresh, so a hit is
   * trusted as up to date. On a miss, fetches and caches when online, or returns null when offline.
   */
  const getSymbolPack = async (packId: string): Promise<SymbolPackDTO | null> => {
    const cached = await symbolPackIdbCache.getPack(packId);

    if (cached) {
      return cached;
    }

    if (genericAppStore.connectivityStatus === ConnectivityStatus.OFFLINE) {
      return null;
    }

    const response = await api.unwrap(api.symbol.getSymbolPack({
      packId,
    }));

    const pack = response.data ?? null;

    if (pack) {
      await symbolPackIdbCache.setPack(pack);
    }

    return pack;
  };

  /**
   * All symbols for a usage type, gathered from each pack's details. A pack that fails to load is
   * skipped so one bad pack doesn't drop the rest.
   */
  const getSymbols = async (usage: SymbolUsageEnum): Promise<SymbolDTO[]> => {
    const packs = await getSymbolPacks(usage);
    const packDetailsResults = await Promise.allSettled(
      packs.map(async pack => await getSymbolPack(pack.id)),
    );

    const symbols: SymbolDTO[] = [];

    for (const packDetailsResult of packDetailsResults) {
      if (packDetailsResult.status !== 'fulfilled' || !packDetailsResult.value?.symbols) {
        continue;
      }

      for (const symbol of packDetailsResult.value.symbols) {
        symbols.push(symbol);
      }
    }

    return symbols;
  };

  /** All symbols marked as favourite for a usage type. */
  const getFavouriteSymbols = async (usage: SymbolUsageEnum): Promise<SymbolDTO[]> => {
    const symbols = await getSymbols(usage);

    return symbols.filter(symbol => symbol.favourite === true);
  };

  /** Revokes and drops every in-memory object URL belonging to a pack. */
  function revokePackMemoryUrls(packId: string): void {
    const prefix = `${packId}:`;

    for (const [key, url] of symbolUrlCache.entries()) {
      if (key.startsWith(prefix)) {
        URL.revokeObjectURL(url);
        symbolUrlCache.delete(key);
      }
    }
  }

  /**
   * Drops a pack's detail caches (metadata, symbol blobs, in-memory URLs) but leaves the pack list
   * untouched, so it's safe to call while reconciling a freshly fetched list.
   */
  async function invalidatePackDetailCache(packId: string): Promise<void> {
    await symbolIdbCache.deleteByPackId(packId);
    await symbolPackIdbCache.delete(packId);
    revokePackMemoryUrls(packId);
  }

  /**
   * Strips the symbols array off a pack DTO (deriving symbolCount from it) so list entries match
   * the shape listSymbolPacks returns and the getSymbolPacks diff stays stable.
   */
  function toPackListEntry(pack: SymbolPackDTO): SymbolPackDTO {
    return {
      ...pack,
      symbolCount: pack.symbols?.length ?? pack.symbolCount,
      symbols: undefined,
    };
  }

  /**
   * Adds or refreshes the pack in each cached usage list it belongs to, and removes it from the
   * others. Lists that were never cached are skipped so they reload in full on next access.
   */
  async function writePackToListCache(pack: SymbolPackDTO): Promise<void> {
    const listEntry = toPackListEntry(pack);

    for (const usage of Object.values(SymbolUsageEnum)) {
      const cachedList = await symbolPackListIdbCache.getList(usage);

      if (!cachedList) {
        continue;
      }

      const withoutPack = cachedList.filter(entry => entry.id !== pack.id);
      const belongsToUsage = pack.usage.includes(usage);

      // Not a member and wasn't listed before: nothing to do for this usage.
      if (!belongsToUsage && withoutPack.length === cachedList.length) {
        continue;
      }

      const nextList = belongsToUsage ? [...withoutPack, listEntry] : withoutPack;

      await symbolPackListIdbCache.setList(usage, nextList);
    }
  }

  /**
   * Applies an updated pack into the cache from an event payload, no refetch. Symbol blobs are
   * kept: a symbol id is stable, so its cached image stays valid across an update.
   */
  async function upsertSymbolPackCache(pack: SymbolPackDTO): Promise<void> {
    await symbolPackIdbCache.setPack(pack);
    await writePackToListCache(pack);
  }

  /**
   * Applies a created pack from an event payload. A re-upload can reuse an existing pack id with
   * brand-new symbol ids, so the old symbol blobs and URLs are dropped first (a no-op for a new pack).
   */
  async function replaceSymbolPackCache(pack: SymbolPackDTO): Promise<void> {
    await symbolIdbCache.deleteByPackId(pack.id);
    revokePackMemoryUrls(pack.id);
    await upsertSymbolPackCache(pack);
  }

  /** Drops a deleted pack's detail caches and removes it from every cached usage list. */
  async function removeSymbolPackFromCache(packId: string): Promise<void> {
    await invalidatePackDetailCache(packId);

    for (const usage of Object.values(SymbolUsageEnum)) {
      const cachedList = await symbolPackListIdbCache.getList(usage);

      if (!cachedList) {
        continue;
      }

      const nextList = cachedList.filter(entry => entry.id !== packId);

      if (nextList.length !== cachedList.length) {
        await symbolPackListIdbCache.setList(usage, nextList);
      }
    }
  }

  /** Removes cached packs and their symbols whose id is not in the valid set. */
  async function evictPacksNotIn(validPackIds: Set<string>): Promise<void> {
    await symbolIdbCache.deletePacksNotIn(validPackIds);
    await symbolPackIdbCache.deletePacksNotIn(validPackIds);
  }

  /**
   * Warms the cache for offline use: fetches the marker and track packs, evicts stale ones, then
   * downloads every symbol into IndexedDB.
   */
  async function prefetchSymbolPacks(): Promise<void> {
    const [markerPacks, trackPacks] = await Promise.all([
      getSymbolPacks(SymbolUsageEnum.MARKER),
      getSymbolPacks(SymbolUsageEnum.TRACK),
    ]);

    const packs = [...markerPacks, ...trackPacks];

    await evictPacksNotIn(new Set(packs.map(pack => pack.id)));

    const packDetails = await Promise.all(packs.map(async pack => await getSymbolPack(pack.id)));

    const symbolJobs: { packId: string; symbolId: string }[] = [];

    for (const pack of packDetails) {
      if (!pack || pack.symbols == null) {
        continue;
      }

      for (const symbol of pack.symbols) {
        symbolJobs.push({ packId: pack.id, symbolId: symbol.id });
      }
    }

    await Promise.allSettled(
      symbolJobs.map(async job => await loadSymbolRecord(job.packId, job.symbolId)),
    );
  }

  /**
   * Logs storage usage and quota for monitoring, and requests persistent storage where supported
   * to reduce the chance of the browser evicting the cache.
   */
  async function reportStorageEstimate(): Promise<void> {
    try {
      const storage = navigator.storage;
      const hasPersistedSupport = typeof storage?.persisted === 'function';
      const hasPersistSupport = typeof storage?.persist === 'function';
      const hasStorageEstimateSupport = typeof storage?.estimate === 'function';

      let persisted: boolean | undefined;

      if (hasPersistedSupport) {
        persisted = await storage.persisted();

        if (!persisted && hasPersistSupport) {
          persisted = await storage.persist();
        }
      }

      if (hasStorageEstimateSupport) {
        const { usage, quota } = await storage.estimate();

        appLogger.info({
          message: 'Symbol cache storage estimate',
          context: {
            usage,
            quota,
            persisted,
          },
        });
      }
    }
    catch (error) {
      appLogger.warn({
        message: 'Storage estimate failed',
        sensitiveContext: {
          error: error instanceof Error ? { name: error.name, message: error.message } : { value: String(error) },
        },
      });
    }
  }

  /** Reports storage estimates and prefetches all symbol packs into IndexedDB. */
  async function warmSymbolCache(): Promise<void> {
    try {
      await reportStorageEstimate();
      await symbolIdbCache.ready();
      await prefetchSymbolPacks();
    }
    catch (error) {
      console.warn('Symbol cache warm-up failed', error);
    }
  }

  return {
    getSymbolObjectUrl,
    getSymbolPacks,
    getSymbolPack,
    getSymbols,
    getFavouriteSymbols,
    isCached,
    getCachedUrl,
    revokeSymbol,
    cleanup,
    upsertSymbolPackCache,
    replaceSymbolPackCache,
    removeSymbolPackFromCache,
    warmSymbolCache,
  };
});
